import React, { useEffect, useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  Typography,
  Box,
  IconButton,
  Button,
  Tooltip,
} from "@mui/material";
import ThumbUpIcon from "@mui/icons-material/ThumbUp";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import { green, red } from "@mui/material/colors";
import { Helmet } from "react-helmet-async";
import Pagination from "../../../Pagination";
import EditServiceProviderModal from "./EditServiceProviderModal";
import LoadingAnimation from "../../../Animations/LoadingAnimation";
import {
  cardOutline,
  fonts,
  headingFonts,
  noOutline,
} from "../../../../Styles/Styles";
import RejectReasonModal from "./RejectReasonModal";

export default function ServiceProvidersManagement() {
  const [serviceProviders, setServiceProviders] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [itemsPerPage] = useState(10); // Items per page
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [openEditModal, setOpenEditModal] = useState(false);
  const [selectedProvider, setSelectedProvider] = useState(null); // Store the provider being edited
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [rejectingProviderId, setRejectingProviderId] = useState(null);

  const fetchServiceProviders = async (page = 1) => {
    setLoading(true);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_SERVER_BASE_URL}/serviceProviders/all?page=${page}&limit=${itemsPerPage}`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error(`Error: ${response.status}`);
      }

      const data = await response.json();
      setServiceProviders(data.serviceProviders);
      setTotalPages(data.totalPages);
      setCurrentPage(data.currentPage);
    } catch (err) {
      console.error("Error fetching service providers:", err);
      setError("Failed to load service providers. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleVerification = async (providerId, isVerified) => {
    if (!isVerified) {
      setRejectingProviderId(providerId);
      setRejectDialogOpen(true);
      return;
    }
    await updateVerificationStatus(providerId, isVerified);
  };

  const handleRejectConfirm = async (reason) => {
    await updateVerificationStatus(rejectingProviderId, false, reason);
    setRejectDialogOpen(false);
    setRejectingProviderId(null);
  };

  const updateVerificationStatus = async (
    providerId,
    isVerified,
    rejectionReason = ""
  ) => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_SERVER_BASE_URL}/serviceProviders/verify/${providerId}`,
        {
          method: "PATCH",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
          body: JSON.stringify({ isVerified, rejectionReason }),
        }
      );

      if (response.ok) {
        setServiceProviders((prev) =>
          prev.map((provider) =>
            provider._id === providerId
              ? { ...provider, isVerified, rejectionReason }
              : provider
          )
        );
      } else {
        console.error("Failed to update verification status");
      }
    } catch (err) {
      console.error("Error updating verification status:", err);
    }
  };

  const handleEdit = (provider) => {
    setSelectedProvider(provider);
    setOpenEditModal(true);
  };

  const handleDelete = async (providerId) => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_SERVER_BASE_URL}/serviceProviders/${providerId}`,
        {
          method: "DELETE",
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );

      if (response.ok) {
        // Remove the deleted provider from the state
        setServiceProviders((prev) =>
          prev.filter((provider) => provider._id !== providerId)
        );
      } else {
        console.error("Failed to delete service provider");
      }
    } catch (err) {
      console.error("Error deleting provider:", err);
    }
  };

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
  };

  useEffect(() => {
    fetchServiceProviders(currentPage);
  }, [currentPage]);

  if (error) {
    return (
      <Typography
        variant="h6"
        color="error"
        sx={{ textAlign: "center", mt: 4 }}
      >
        {error}
      </Typography>
    );
  }

  if (loading) {
    return <LoadingAnimation />;
  }

  return (
    <Box sx={{ padding: 3 }}>
      <Helmet>
        <title>Service Providers Management - Listed.ky</title>
        <meta
          name="description"
          content="Service Providers Management - Listed.ky"
        />
      </Helmet>

      <Typography variant="h4" gutterBottom sx={{ ...headingFonts }}>
        Service Providers Management
      </Typography>
      <TableContainer sx={{ ...cardOutline }}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell sx={{ ...headingFonts }}>Name</TableCell>
              <TableCell sx={{ ...headingFonts }}>Email</TableCell>
              <TableCell sx={{ ...headingFonts }}>Phone</TableCell>
              <TableCell sx={{ ...headingFonts }}>Services</TableCell>
              <TableCell sx={{ ...headingFonts }}>Verified</TableCell>
              <TableCell sx={{ ...headingFonts }}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {serviceProviders.map((provider) => (
              <TableRow key={provider._id}>
                <TableCell sx={{ ...headingFonts }}>{provider.name}</TableCell>
                <TableCell sx={{ ...fonts }}>
                  {provider.contactInfo.email}
                </TableCell>
                <TableCell sx={{ ...fonts }}>
                  {provider.contactInfo.countryCode +
                    " " +
                    provider.contactInfo.phone}
                </TableCell>
                <TableCell sx={{ ...fonts }}>
                  {provider.skills.map((skill) => (
                    <div key={skill.parentServiceId._id}>
                      <strong>Parent Service:</strong>{" "}
                      {skill.parentServiceId.name}
                      {skill.subServiceIds.length > 0 && (
                        <>
                          <br />
                          <strong>Sub-Services:</strong>{" "}
                          {skill.subServiceIds
                            .map((subService) => subService.name)
                            .join(", ")}
                        </>
                      )}
                    </div>
                  ))}
                </TableCell>
                <TableCell>
                  {provider.isVerified ? (
                    <Tooltip title={"Verified Service Provider"} arrow>
                      <CheckCircleIcon sx={{ color: green[500] }} />
                    </Tooltip>
                  ) : (
                    <Tooltip title={"UnVerified Service Provider"} arrow>
                      <CancelIcon sx={{ color: red[500] }} />
                    </Tooltip>
                  )}
                </TableCell>
                <TableCell>
                  <Box sx={{ display: "flex", flexWrap: "nowrap", gap: 1 }}>
                    <Tooltip title="Approve">
                      <IconButton
                        onClick={() => handleVerification(provider._id, true)}
                        sx={{ ...noOutline }}
                      >
                        <CheckCircleIcon sx={{ color: green[500] }} />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Reject">
                      <IconButton
                        onClick={() => handleVerification(provider._id, false)}
                        sx={{ ...noOutline }}
                      >
                        <CancelIcon sx={{ color: red[500] }} />
                      </IconButton>
                    </Tooltip>

                    <IconButton
                      onClick={() => handleEdit(provider)}
                      sx={{
                        color: "var(--primary-color)",
                        ...noOutline,
                      }}
                    >
                      <EditIcon />
                    </IconButton>

                    <IconButton
                      onClick={() => handleDelete(provider._id)}
                      color="error"
                      sx={{ ...noOutline }}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Box>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      {totalPages > 1 && (
        <Box sx={{ mt: 6, display: "flex", justifyContent: "center" }}>
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={handlePageChange}
          />
        </Box>
      )}

      {/* Reject Reason Modal */}
      <RejectReasonModal
        open={rejectDialogOpen}
        onClose={() => setRejectDialogOpen(false)}
        onConfirm={handleRejectConfirm}
      />

      {/* Edit Modal */}
      {selectedProvider && (
        <EditServiceProviderModal
          open={openEditModal}
          data={selectedProvider}
          onClose={() => setOpenEditModal(false)}
          onSave={() => {
            setOpenEditModal(false);
            fetchServiceProviders(currentPage); // Refresh data after save
          }}
        />
      )}
    </Box>
  );
}
